import React, {useEffect, useRef, useState} from 'react';
import {Link, useLocation, useNavigate, useOutletContext, useParams, useSearchParams} from 'react-router-dom';
import TitleMain from "../../../components/Title/TitleMain";
import {useModal} from "../../../contexts/ModalContext";
import {useUser} from "../../../contexts/UserContext";
import AxiosConfig from "../../../services/AxiosConfig";
import {useBreadcrumb} from "../../../contexts/BreadcrumbContext";
import {generateBreadcrumbSegmentsAdmin} from "../../../services/BreadcrumbService";
import SeanceCreation from "../../../components/Admin/Seances/SeanceCreation";
import SeancesVue from "../../../components/Admin/Seances/SeancesVue";
import {useLoading} from "../../../contexts/LoadingContext";

function AdminSeancesPage() {
    const {user} = useUser();
    const {showModal, hideModal} = useModal();
    const [myApiData, setMyApiData] = useState([]);
    const {entrepriseId, contratId, activiteId} = useParams();
    const { updateDynamicBreadcrumb } = useBreadcrumb();
    const [entreprise, setEntreprise] = useState({});
    const [contrat, setContrat] = useState({});
    const [activite, setActivite] = useState({});
    const [searchParams] = useSearchParams();
    const mode = searchParams.get('mode') || 'consultation';
    const utilisateur = searchParams.get('filter_utilisateur') || null;
    const coach = searchParams.get('filter_coach') || null;
    const { isLoading, setLoading } = useLoading();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const [page, setPage] = useState(parseInt(queryParams.get("page")) || 1);
    const [totalPages, setTotalPages] = useState(100);
    const [totalSeances, setTotalSeances] = useState(null);
    const navigate = useNavigate();
    const [sortBy, setSortBy] = useState([]);
    const prevSortBy = useRef(sortBy);

    const putActifOpenModal = (row) => {
        const text = row.actif ? "supprimer cette séance" : "activer cette séance";
        showModal('confirm', {
            message: `Voulez-vous vraiment ${text} ?`,
            onConfirm: async () => {
                try {
                    const response = await AxiosConfig.post(`/admin/entreprises/${row.entreprise.id}/contrats/${row.contrat.id}/activites/${row.activite.id}/seances/${row.id}/actif`, {actif: !row.actif});
                    if (response.data.success) {
                        setTimeout(async () => {
                            getSeances();
                        }, 500);
                    }
                    handleSuccess("Statut modifié avec succès");
                } catch (error) {
                    console.error('error', error);
                    handleError();
                }
            }
        });
    }

    const handleSubmitSeanceStatus = async (values, {setSubmitting}) => {
        try {
            const response = await AxiosConfig.post(`/admin/seances/${values.id}/status`, values);

            if (response.data.success) {
                handleSuccess(response.data.message);
                getSeances();
            } else {
                console.error('error', response.data);
                handleError("Erreur : " + response.data.message);
            }
        } catch (error) {
            console.error('error', error);
            handleError();
        }
    };

    const onSortChange = ({ sortBy }) => {
        if (JSON.stringify(sortBy) !== JSON.stringify(prevSortBy.current)) {
            prevSortBy.current = sortBy;
            setSortBy(sortBy);
        }
    };

    const getInitialSortBy = () => {
        const sortBy = [];
        queryParams.getAll('sort_by').forEach((id, index) => {
            sortBy.push({ id, desc: queryParams.getAll('sort_desc')[index] === 'true' });
        });
        return sortBy;
    };

    const seancesVue = <SeancesVue
        entreprise={entreprise}
        contrat={contrat}
        activite={activite}
        queryParams={queryParams}
        seances={myApiData}
        putActifOpenModal={putActifOpenModal}
        handleSubmitSeanceStatus={handleSubmitSeanceStatus}
        isLoading={isLoading}
        setPage={setPage}
        page={page}
        totalPage={totalPages}
        totalSeances={totalSeances}
        serverSideSorting={true}
        onSortChange={onSortChange}
        initialSortBy={getInitialSortBy()}
    />;
    const modeOptions = {
        'creation': <SeanceCreation contrat={contrat} entreprise={entreprise} activite={activite} />,
        'consultation': seancesVue,
        'default': seancesVue
    }
    const ComponentToRender = modeOptions[mode] || modeOptions['default'];

    const handleSuccess = (children = "Note enregistrée avec succès") => {
        showModal('success', {children: children, timeout: 1500});
    };
    const handleError = (children = "Une erreur est survenue") => {
        showModal('error', {children: children, timeout: 1500});
    };

    const getSeances = async () => {
        setLoading(true);

        const params = location.search ? new URLSearchParams(location.search) : new URLSearchParams();
        if (utilisateur) params.append('utilisateur', utilisateur);
        if (coach) params.append('coach', coach);
        sortBy.forEach(sort => {
            params.append('sort_by', sort.id);
            params.append('sort_desc', sort.desc);
        });

        const url = `/admin/entreprises/${entrepriseId}/contrats/${contratId}/activites/${activiteId}/seances?${params.toString()}`;

        try {
            const response = await AxiosConfig.get(url);
            const newSeances = response.data.mySeances;
            newSeances.map(seance => {
                seance.entreprise = response.data.myEntreprise;
                seance.contrat = response.data.myContrat;
                seance.activite = response.data.myActivite;
                return seance;
            });
            setMyApiData(newSeances);
            setContrat(response.data.myContrat);
            setEntreprise(response.data.myEntreprise);
            setActivite(response.data.myActivite);
            setTotalPages(response.data.totalPages);
            setTotalSeances(response.data.totalSeances);
        } catch (error) {
            console.error('error', error);
        }
        setLoading(false);
    };

    useEffect(() => {
        updateDynamicBreadcrumb(generateBreadcrumbSegmentsAdmin({ entreprise, contrat, activite }));
    }, [ entreprise, contrat, activite ]);

    useEffect(() => {
        if (entrepriseId && contratId && activiteId) {
            getSeances();
        }
    }, [location.search]);

    useEffect(() => {
        let queryParamsChange = false;
        const newQueryParams = new URLSearchParams(location.search);

        if (page && mode === 'consultation') {
            newQueryParams.set('page', page);
            queryParamsChange = true;
        }

        if (sortBy.length > 0 && mode === 'consultation') {
            newQueryParams.delete('sort_by');
            newQueryParams.delete('sort_desc');
            sortBy.forEach(sort => {
                newQueryParams.append('sort_by', sort.id);
                newQueryParams.append('sort_desc', sort.desc);
            });
            queryParamsChange = true;
        }

        if (queryParamsChange) {
            navigate(`${location.pathname}?${newQueryParams.toString()}`);
        }
    }, [page, sortBy, mode, navigate, location.pathname]);

    return (
        entreprise && entreprise.id && contrat && contrat.id && activite && activite.id && ComponentToRender
    )
}

export default AdminSeancesPage;
